import type {
	PortalCatalogItemDto,
	StockDisplayMode,
} from '@supply-mover-2-0/shared';

/**
 * PD-16750 — the storefront's quantity line, worded for the visitor's stock
 * display mode:
 *   - exact:          "37 in stock"
 *   - capped, ranges: "100+ in stock", "11-50 in stock"
 *   - levels:         "Medium stock", or "Out of Stock" alone when nothing
 *                     is on hand ("Out of Stock stock" would read wrong)
 *
 * Returns the parts to translate rather than a sentence, so each piece goes
 * through t() the way the rest of the storefront's text does. Null when
 * there is no quantity to show.
 */
export type StockQuantityLineModel =
	/** Renders "{value} in stock". */
	| { kind: 'inStock'; value: string }
	/** Renders "{value} stock" (a level name). */
	| { kind: 'level'; value: string }
	/** Renders "{value}" on its own. */
	| { kind: 'label'; value: string };

export type StockQuantityFieldsModel = Pick<
	PortalCatalogItemDto,
	'quantityOnHand' | 'quantityOnHandDisplay' | 'quantityShownMinimum'
>;

export function resolveStockQuantityLine(
	fields: StockQuantityFieldsModel,
	mode: StockDisplayMode,
): StockQuantityLineModel | null {
	const masked = fields.quantityOnHandDisplay;

	if (masked != null && masked.trim() !== '') {
		if (mode !== 'levels') {
			return { kind: 'inStock', value: masked };
		}

		return fields.quantityShownMinimum === 0
			? { kind: 'label', value: masked }
			: { kind: 'level', value: masked };
	}

	if (fields.quantityOnHand != null) {
		return { kind: 'inStock', value: String(fields.quantityOnHand) };
	}

	return null;
}
